import FileSaver from "file-saver";
import { RootRoute } from "@app/const";
import { defaultServiceContext } from "@app/contexts";
import BaseComponent from "@app/form_components/BaseComponent";
import { ObjectClass } from "@app/types";
import {
  createUuid,
  navigateToRoute,
  renderByRoute
} from "@app/utilities";
import { ReactDOMComponents } from "@app/utilities/ReactDOMComponents";
import Seed from "@app/utilities/Seed";
import CSVUploadButtonAndPreview from "@app/widgets/CSVUploadButtonAndPreview";
import NewElementDialog from "@app/widgets/dialogs/NewElementDialog";

const { avsService } = defaultServiceContext;
const genericRoutingDesignName = 'GenericRouting';

export class ImportIntentPage extends BaseComponent {
  /**
   * Dialog for CSV upload and preview
   * @module ImportIntentCSV
   * @param {Object} props React component props
   */
  constructor(props) {
    super(props);

    this.state = {
      parsedUploadedFile: '',
    };

    this.downloadTemplate = this.downloadTemplate.bind(this);
    this.onSelectFile = this.onSelectFile.bind(this);
    this.getValidatedRow = this.getValidatedRow.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.reactDOMComponents = new ReactDOMComponents();
  }

  /**
   * Validates each field on the uploaded CSV file making sure that each row
   * has the proper value based on the provided header.
   * @param {object} row The row to evaluate.
   * @param {string} header The header to evaluate the row against.
   * @returns {object}
   */
  getValidatedRow(row, header) {
    const rowValue = row[header];
    let validated = {};

    switch (header) {
      case 'IntentName':
      case 'ModuleName':
        if (rowValue === '') {
          // Invalid (value is empty)
          validated = { value: 'Cannot be empty', class: 'red' };
        } else {
          const uppercaseValue = rowValue.toUpperCase().replace(/ /g, '_');
          const isUppercaseValid = !!uppercaseValue.match(/^[A-Z][A-Z0-9_]*$/);

          if (isUppercaseValid && rowValue !== uppercaseValue) {
            // Valid but there are some text difference.
            validated = { value: uppercaseValue, class: 'blue' };
          } else if (!isUppercaseValid) {
            // Invalid (cannot be uppercased with underscores)
            validated = { value: uppercaseValue, class: 'red' };
          } else {
            // Valid.
            validated = { value: rowValue, class: 'black' };
          }
        }
        break;
      case 'IntentDescription':
      case 'ModuleDescription':
        if (rowValue === '') {
          // Invalid (value is empty)
          validated = { value: 'Cannot be empty', class: 'red' };
        } else {
          // Valid.
          validated = { value: rowValue, class: 'black' };
        }
        break;
      case 'TransferFlag':
        if (rowValue === '') {
          // If value is empty then it gets transformed to a string boolean value.
          validated = { value: 'True', class: 'blue' };
        }
        break;
      case 'TransferDestination':
      case 'TransferReason':
        // Optional, so it's always valid.
        validated = { value: rowValue, class: 'black' };
        break;
      case 'PromptText':
        // If there's value, it cannot be empty.
        if (rowValue !== '' && rowValue.includes('{{')) {
          validated = { value: rowValue, class: 'red' };
        } else {
          // Valid.
          validated = { value: rowValue, class: 'black' };
        }
    }

    return validated;
  }

  /**
   * Download an intent CSV template file.
   */
  downloadTemplate() {
    const intentHeaderFields = 'IntentName,IntentDescription,ModuleName,ModuleDescription,PromptText,TransferDestination,TransferReason,TransferFlag\r\nMAKE_PAYMENT,Caller wants to make a payment,MAKE_PAYMENT,Making a payment,I understand you would like to make a payment...,8003992039,Business Rule,TRUE\r\nTECH_SUPPORT,Caller needs tech support,TECH_SUPPORT,Generic tech support,I can help with tech support...,,,FALSE';
    var blob = new Blob([intentHeaderFields], { type: 'csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'Intent.csv');
  }

  /**
   * Handler for selecting a file from the `CSVUploadButtonAndPreview` component.
   * @param {boolean} isValidFile Whether the selected file is a valid CSV or not.
   * @param {Papa.ParseResult<T>} parsedFile The parsed version of the uploaded CSV file.
   */
  onSelectFile(isValidFile, parsedFile) {
    if (!isValidFile) {
      window.apm.notification.post('error', window.l.notification.csv_invalid);
      return;
    }

    this.setState({ parsedUploadedFile: parsedFile });
  }

  /**
   * Handler for the submit action. This first check for some modules on `window.apm`
   * and performs some API requests in case it doesn't find them, then it calls
   * the method that actually generates the intents/modules/prompts.
   */
  async onSubmit() {
    const finishProcess = () => {
      // Process the CSV data and create intents/modules/prompts.
      this.processCSVData();

      // Go back to the canvas tab and show a processing message to users (block the UI).
      // This is shown while the app is creating the intents/modules/prompts
      // out of the uploaded CSV file.
      navigateToRoute(RootRoute.canvas);
      Utility.openApplicationOverlay(window.l.notification.import_processing);
    }
    const moduleExists = window.apm.serialized.appCommonModules.some((el) => {
      return el.application === genericRoutingDesignName;
    });

    if (moduleExists) {
      finishProcess();
      return;
    }

    // Fetch the InteractionsCommon generic routing module in order to then fetch the
    // InteractionsCommon design file to have the common module locally saved and avoid
    // unnecessary/duplicate API requests to AVS when uploading different CSV files.
    const genericRoutingDesign = await avsService.getCommonModuleDesignByName(
      genericRoutingDesignName
    );

    const commonModule = { ...genericRoutingDesign };
    delete commonModule.fillerPrompts;
    delete commonModule.globalErrorHandling;
    delete commonModule.globalExceptionHandling;
    delete commonModule.dtmf_live_agent_override_pattern;
    delete commonModule.languages;
    delete commonModule.voice;
    delete commonModule.language;
    delete commonModule.lastUpdate;
    delete commonModule.designer;
    window.apm.serialized.appCommonModules.push(commonModule);
    // CM TODO: in use?
    NewElementDialog.addCommonModule($('#tl_container'), commonModule);
    finishProcess();
  }

  /**
   * Process the uploaded CSV data and generates the intents/modules/prompts.
   */
  processCSVData() {
    // Get the parsed data.
    const csv = { ...this.state.parsedUploadedFile };
    const { intents, modules, objects } = window.apm.serialized.components;
    const prompts = objects.filter((x) => {
      return x._class === ObjectClass.prompt;
    });

    // Process each row from the uploaded CSV file.
    csv.data.forEach((row) => {
      // Sanity check if the CSV file has an empty line as last row.
      let intentName = row['IntentName'] ? row['IntentName'].trim() : '';
      let moduleName = row['ModuleName'] ? row['ModuleName'].trim() : '';
      if (intentName === '' || moduleName === '') return;

      // Tweak column values.
      const intentDescription = row['IntentDescription'] ? row['IntentDescription'].trim() : 'Blank intent';
      const moduleDescription = row['ModuleDescription'] ? row['ModuleDescription'].trim() : 'Blank module';
      intentName = intentName.toUpperCase().replace(/ /g, '_');
      moduleName = moduleName.toUpperCase().replace(/ /g, '_');

      // Grab existing module/intent/prompt to make sure to not create duplicates.
      let newIntent = intents.find(x => x.name === intentName) || {};
      let newModule = modules.find(module => module.name === moduleName) || {};
      let newPrompt = prompts.find(prompt => prompt.name === `${intentName}_ROUTING_PROMPT`) || {};

      // Intent does not exist yet.
      if (Object.keys(newIntent).length === 0) {
        newIntent = Seed.getBlankIntentObject(intentName, intentDescription, true);
        window.apm.addNode(newIntent, false);
      }

      // Module does not exist yet.
      if (Object.keys(newModule).length === 0) {
        newModule = Seed.getBlankModuleObject(moduleName, moduleDescription);
        Utility.addNodeToTreeDomElement('modules', newModule, 'last');

        // Prompt does not exist yet.
        if (Object.keys(newPrompt).length === 0) {
          newPrompt = Seed.getBlankPromptObject(`${intentName}_ROUTING_PROMPT`, row['PromptText']);
          Utility.addNodeToTreeDomElement('Prompt', newPrompt, 'last');
          window.apm.addNode(newPrompt, false);
        }

        // Add additional props to newModule based on the generic routing module.
        const genericRoutingIdx = window.apm.serialized.appCommonModules.findIndex(
          cm => cm.application === genericRoutingDesignName,
        );
        if (genericRoutingIdx !== -1) {
          const genericModuleIdx = window.apm.serialized.components.modules.findIndex(
            mod => mod.props.moduleId === window.apm.serialized.appCommonModules[genericRoutingIdx]._id,
          );
          if (genericModuleIdx !== -1) {
            const genericModule = { ...window.apm.serialized.components.modules[genericModuleIdx] };
            const newModuleNode = Seed.getBlankModuleNodeObject(genericModule.name, genericModule.props);

            // Update new module based on generic routing module.
            newModule.versions[1].flow.nodes.push(newModuleNode);
            newModule.versions[1].flow.nodes[1].props.lastLocalChange = window.apm.timestamp;
            newModule.versions[1].flow.nodes[1].props.ref = genericModule.id;
            newModule.versions[1].flow.nodes[1].props.timeAddedToFlow = window.apm.timestamp;
            newModule.versions[1].flow.nodes[1].props.parentComponentID = genericModule.id;

            (newModule.versions[1].flow.nodes[1].props.config.parameters || []).forEach((param, k) => {
              switch (param.varName) {
                case 'shouldRoute':
                  const transferFlag = row['TransferFlag'];
                  if (transferFlag === '' || transferFlag?.toUpperCase() === 'TRUE') {
                    newModule.versions[1].flow.nodes[1].props.config.parameters[k].value = 'true';
                  } else if (transferFlag?.toUpperCase() === 'FALSE') {
                    newModule.versions[1].flow.nodes[1].props.config.parameters[k].value = 'false';
                  }
                  break;
                case 'transferReason':
                  const transferReason = row['TransferReason'] || '';
                  newModule.versions[1].flow.nodes[1].props.config.parameters[k].value = transferReason;
                  break;
                case 'transferDestination':
                  const transferDescription = row['TransferDestination'] || '';
                  newModule.versions[1].flow.nodes[1].props.config.parameters[k].value = transferDescription;
                  break;
                case 'transferNode':
                  const transferModule = window.apm.serialized.components.modules.find(m => m.name === 'TRANSFER') || {};
                  if (Object.keys(transferModule).length > 0) {
                    newModule.versions[1].flow.nodes[1].props.config.parameters[k].value = 'TRANSFER';
                    newModule.versions[1].flow.nodes[1].props.config.parameters[k].moduleId = transferModule.id;
                  }
                  break;
                case 'promptName':
                  newModule.versions[1].flow.nodes[1].props.config.parameters[k].value = newPrompt.name;
                  newModule.versions[1].flow.nodes[1].props.config.parameters[k].promptId = newPrompt.id;
                  break;
              }
            });

            newModule.versions[1].flow.nodes[1].name = genericModule.name;
          }
        }

        // Create a return to root / exit node on newModule.
        const returnToRootModule = window.apm.serialized.components.objects.find(
          m => m.name === 'RETURN_TO_ROOT' && m._class === ObjectClass.exit,
        ) || {};
        let exitModuleNode = {};
        if (Object.keys(returnToRootModule).length > 0) {
          exitModuleNode = Seed.getBlankReturnToRootExitModuleNode(
            returnToRootModule.name,
            returnToRootModule.id,
            returnToRootModule.props,
          );
        }
        newModule.versions[1].flow.nodes.push(exitModuleNode);

        // Add links/connections to newModule.
        const firstLink = Seed.getBlankModuleFlowLinkObject(
          newModule.versions[1].flow.nodes[0].id,
          newModule.versions[1].flow.nodes[1].id,
          [{ x: 810, y: 141 }, { x: 810, y: 220 }],
        );
        const secondLink = Seed.getBlankModuleFlowLinkObject(
          newModule.versions[1].flow.nodes[1].id,
          newModule.versions[1].flow.nodes[2].id,
          [{ x: 810, y: 281 }, { x: 810, y: 360 }],
        );
        newModule.versions[1].flow.links = [firstLink, secondLink];


        // Finally add newModule into window.apm
        window.apm.addNode(newModule, false);
      }

      // Create global intent if it isn't exist yet
      const currentGlobalIntentIdx = window.apm.serialized.globalIntents.findIndex((x) => x.intent === newIntent.id);
      if (currentGlobalIntentIdx === -1 && newIntent.isGlobalIntent) {
        const globalIntentConditionProperties = Seed.getBlankIntentConditionPropertiesObject(newIntent.id);
        const globalIntentModuleProperties = Seed.getBlankModuleNodeObject(newModule.name, newModule.props);
        delete globalIntentModuleProperties.x;
        delete globalIntentModuleProperties.y;

        window.apm.serialized.globalIntents.push({
          id: createUuid(),
          createdTime: window.apm.timestamp,
          intent: newIntent.id,
          module: newModule.id,
          conditionProperties: globalIntentConditionProperties,
          moduleProperties: globalIntentModuleProperties,
        });
      }
    });

    // Update tree UI (application panel).
    window.apm.refreshApplicationTree(true);

    // Re-render.
    renderByRoute(RootRoute.intents);

    // Close the overlay (unblock the UI) and show a success message to users
    // after all intents/modules/prompts have been created.
    Utility.closeApplicationOverlay(0);
    this.setState({ parsedUploadedFile: '' });
    window.apm.notification.post('success', window.l.notification.import_success);
  }

  /**
   * React render
   * @returns {JSX.Element}
   */
  render() {
    let hasTransferError = false;
    const transferIndex = window.apm.serialized.components.modules.findIndex(mod => mod.name === 'TRANSFER');
    const { parsedUploadedFile } = this.state;
    if (transferIndex === -1 && parsedUploadedFile !== '') {
      hasTransferError = true;
      window.apm.notification.post('error', window.l.notification.transfer_module);
    }

    // If there isn't a transfer module in the current app we don't show the submit button.
    const onSubmitHandler = hasTransferError ? false : this.onSubmit;

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="script-header" style={{ fontWeight: "normal", display: "block" }}>
            <div style={{ height: '200px', fontSize: '14px' }}>
              <span style={{ fontWeight: "bold" }}>
                1) Create CSV
              </span>
              <div style={{ textAlign: "center", paddingTop: "30px" }}>
                Download the intent import CSV template and populate of the columns.
                <br />
                Do NOT add aditional colums or reorder the columns. Use "true" or "false" for the TransferFlag column.
              </div>
              <div style={{ paddingTop: "20px", textAlign: "center" }}>
                <button
                  className="w2ui-btn primary"
                  onClick={this.downloadTemplate}
                >
                  Download Template
                </button>
              </div>
            </div>
            <div className="script-wrapper">
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                2) Import CSV
              </div>
              <CSVUploadButtonAndPreview
                onSelectFile={this.onSelectFile}
                previewStyles={{ width: "100%", overflow: "auto", maxHeight: "450px" }}
                validateRowFunction={this.getValidatedRow}
                onDetectInvalidRows={() => window.apm.notification.post('error', window.l.notification.fix_csv_errors)}
                onSubmit={onSubmitHandler}
                onClose={() => this.setState({ parsedUploadedFile: '' })}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
